import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Phone, MessageSquare, AlertTriangle, MapPin } from "lucide-react";

export default function Contacts() {
  const [crisis, setCrisis] = useState([]);
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/contacts/crisis"), api.get("/safety-plan")]).then(([c, p]) => {
      setCrisis(c.data);
      setPlan(p.data);
    });
  }, []);

  const personalSections = plan ? [
    { title: "People to call for support", key: "trusted_supports", icon: Phone, tone: "bg-teal-50 text-teal-900" },
    { title: "Professional support", key: "professional_supports", icon: MapPin, tone: "bg-amber-50 text-amber-900" },
    { title: "People for distraction", key: "social_distractions", icon: MessageSquare, tone: "bg-slate-100 text-slate-900" },
  ] : [];

  return (
    <div className="space-y-6 fade-up" data-testid="contacts-page">
      <header>
        <h1 className="font-display text-3xl font-bold text-slate-900">Crisis &amp; support contacts</h1>
        <p className="text-slate-600 mt-2 max-w-xl">UK crisis lines and your own trusted people — one tap away.</p>
      </header>

      {/* UK Crisis Contacts */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="uk-crisis-card">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          <h2 className="font-display text-lg font-bold text-slate-900">UK Crisis lines (24/7)</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {crisis.map((c) => (
            <div key={c.id} className={`rounded-xl border ${c.category === "emergency" ? "border-red-200 bg-red-50" : "border-slate-200 bg-slate-50"} p-4`} data-testid={`crisis-card-${c.id}`}>
              <div className="font-semibold text-slate-900">{c.name}</div>
              <div className="text-xs text-slate-600 mt-0.5">{c.description}</div>
              {c.phone && (
                <a
                  href={c.sms ? `sms:${c.phone}&body=SHOUT` : `tel:${c.phone}`}
                  data-testid={`crisis-action-${c.id}`}
                  className={`mt-3 inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold ${
                    c.category === "emergency" ? "bg-red-600 hover:bg-red-700 text-white" : "bg-teal-700 hover:bg-teal-800 text-white"
                  }`}
                >
                  {c.sms ? <MessageSquare className="w-4 h-4" /> : <Phone className="w-4 h-4" />}
                  {c.sms ? `Text ${c.phone}` : `Call ${c.phone}`}
                </a>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Personal contacts from safety plan */}
      {personalSections.map((sec) => {
        const items = (plan?.[sec.key] || []);
        if (items.length === 0) return null;
        return (
          <div key={sec.key} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid={`contacts-${sec.key}`}>
            <div className="flex items-center gap-2 mb-4">
              <div className={`w-9 h-9 rounded-xl ${sec.tone} flex items-center justify-center`}>
                <sec.icon className="w-4 h-4" />
              </div>
              <h2 className="font-display text-lg font-bold text-slate-900">{sec.title}</h2>
            </div>
            <div className="space-y-2">
              {items.map((c, i) => (
                <div key={i} className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
                  <div>
                    <div className="font-medium text-slate-900">{c.name}</div>
                    <div className="text-xs text-slate-500">{c.relationship} · {c.phone}</div>
                  </div>
                  <div className="flex gap-2">
                    <a data-testid={`call-${sec.key}-${i}`} href={`tel:${c.phone}`} className="bg-teal-700 hover:bg-teal-800 text-white rounded-xl px-3 py-2 text-sm flex items-center gap-1">
                      <Phone className="w-4 h-4" /> Call
                    </a>
                    {sec.key === "trusted_supports" && (
                      <a data-testid={`sms-${sec.key}-${i}`} href={`sms:${c.phone}&body=I%20am%20struggling%20at%20the%20moment%20and%20would%20appreciate%20some%20support.`} className="bg-white border border-slate-300 rounded-xl px-3 py-2 text-sm flex items-center gap-1 text-slate-700">
                        <MessageSquare className="w-4 h-4" /> SMS
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {plan && (plan.trusted_supports?.length === 0 && plan.professional_supports?.length === 0) && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 text-sm text-slate-600" data-testid="contacts-empty">
          Add your own trusted people and professionals from your Safety Plan to see them here.
        </div>
      )}
    </div>
  );
}
